import { Plan } from '../types'
import type { AccountDetails } from './csvImport'
import type { PdfStatement } from './pdfImport'
import type { SpendingShare } from './spending'

/**
 * Everything the browser sends to the server.
 *
 * Same origin in both development (via the Vite proxy) and production (one
 * container serves the bundle and the API), so authentication is a plain
 * session cookie the browser handles for us — there is no token to store,
 * refresh, or accidentally leave in localStorage.
 */

export type ApiErrorCode =
  | 'not_authenticated'
  | 'authentication_failed'
  | 'forbidden'
  | 'invalid'
  | 'invalid_token'
  | 'conflict'
  | 'rate_limited'
  | 'bad_request'
  | 'not_found'
  | 'server_error'
  | 'offline'
  | 'unknown'

/** A response the server answered with — a 4xx or 5xx that came back as JSON. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    /** Per-field messages, when the failure was a form the user can fix. */
    readonly fields?: Record<string, string>,
    /** The server's own copy, sent back with a 409 so the caller can offer a choice. */
    readonly current?: RemoteDocument | null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the request never reached the server — offline, DNS, server down. */
  get isNetworkFailure() {
    return this.code === 'offline'
  }
}

export interface AuthUser {
  id: string
  email: string
  verified: boolean
  createdAt: string
}

/** Someone signed in to /admin (SM-20) — an admin account, never a user account. */
export interface AdminAccount {
  email: string
}

/** The item types a name can be suggested for. */
export type SuggestionKind = 'income' | 'expense' | 'budget' | 'oneoff'

/** A name offered as you type, and the category picking it fills in (SM-21). */
export interface NameSuggestion {
  kind: SuggestionKind
  name: string
  category: string | null
}

export interface AdminNameSuggestion extends NameSuggestion {
  id: string
}

/** Which of the names people type an admin is looking at (SM-22). */
export type AdminLearnedNameFilter = 'waiting' | 'near' | 'rejected'

/** A name people have typed that isn't a suggestion yet, with the category most of them give it. */
export interface AdminLearnedName extends AdminNameSuggestion {
  people: number
}

export type AdminMerchantFilter = 'all' | 'suggested' | 'near' | 'flagged'

export interface AdminMerchant {
  id: string
  key: string
  name: string
  people: number
  flagged: boolean
  /** For spending statistics (SM-18): none means nothing is shared for it. */
  category: string | null
  sensitive: boolean
}

/** A saved monthly spending figure (SM-18). Amounts are pence per person per month. */
export interface SpendFigure {
  subject: string
  name: string
  category: string | null
  people: number
  /** All of the group's spend, whole pounds. */
  total: number
  mean: number
  median: number
  p25: number
  p75: number
  visits: number
}

export interface AdminSpending {
  months: string[]
  month: string | null
  figures: { shop: SpendFigure[]; category: SpendFigure[]; pair: SpendFigure[] } | null
  minPeople: number
}

export interface RemoteDocument {
  slot: DocumentSlot
  /** Null for an account that has never saved anything. */
  data: Plan | null
  /** 0 when nothing is stored yet; every save increments it. */
  version: number
  updatedAt: string | null
}

export type DocumentSlot = 'main' | 'sandbox'

interface RequestOptions {
  method?: string
  body?: unknown
  /** Lets a save still go out while the tab is closing. */
  keepalive?: boolean
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, keepalive, signal } = options

  // A file upload goes as multipart; the browser sets that content type itself.
  const form = body instanceof FormData
  let response: Response
  try {
    response = await fetch(path, {
      method,
      signal,
      keepalive,
      headers: body === undefined || form ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : form ? body : JSON.stringify(body),
      // Explicit rather than relying on the default: if this app is ever
      // served from a different origin to the API, omitting the cookie is the
      // kind of failure that looks like "randomly signed out".
      credentials: 'same-origin',
    })
  } catch {
    // fetch only rejects when the request never completed. A 500 comes back as
    // a resolved promise, so anything landing here is the network or the
    // server being unreachable — worth telling apart, because retrying helps.
    throw new ApiError(0, 'offline', "Couldn't reach the server.")
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const code = (payload?.error as ApiErrorCode) ?? 'unknown'
    throw new ApiError(
      response.status,
      code,
      payload?.message ?? `Request failed (${response.status}).`,
      payload?.fields,
      payload?.current ?? null,
    )
  }

  return payload as T
}

export const api = {
  /** Answers `{ user: null }` rather than failing when signed out. */
  me: () => request<{ user: AuthUser | null }>('/api/auth/me'),

  register: (email: string, password: string) =>
    request<{ user: AuthUser }>('/api/auth/register', { method: 'POST', body: { email, password } }),

  login: (email: string, password: string) =>
    request<{ user: AuthUser }>('/api/auth/login', { method: 'POST', body: { email, password } }),

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  resendVerification: () =>
    request<{ status: string }>('/api/auth/verify-email/resend', { method: 'POST' }),

  forgotPassword: (email: string) =>
    request<{ status: string; message: string }>('/api/auth/password/forgot', {
      method: 'POST',
      body: { email },
    }),

  resetPassword: (token: string, password: string) =>
    request<{ status: string }>('/api/auth/password/reset', {
      method: 'POST',
      body: { token, password },
    }),

  getState: (slot: DocumentSlot, signal?: AbortSignal) =>
    request<RemoteDocument>(`/api/state?slot=${slot}`, { signal }),

  /**
   * Pass the version this state was built from and the server refuses to
   * overwrite a newer one; pass null to overwrite whatever is there, which is
   * what "keep mine" in the conflict banner means.
   */
  putState: (slot: DocumentSlot, data: Plan, version: number | null, keepalive = false) =>
    request<RemoteDocument>(`/api/state?slot=${slot}`, {
      method: 'PUT',
      body: version === null ? { data } : { data, version },
      keepalive,
    }),

  deleteState: (slot: DocumentSlot) =>
    request<RemoteDocument>(`/api/state?slot=${slot}`, { method: 'DELETE' }),

  /**
   * Where to listen for this person's saves made elsewhere. Also sets the
   * cookie the hub checks, so it's called again whenever the stream is refused.
   */
  live: () => request<{ enabled: boolean; hub?: string; topic?: string }>('/api/live'),

  /** Clean names from the shared catalogue, for whichever of these merchant keys enough people have shared. */
  merchantSuggestions: (keys: string[]) =>
    request<{ suggestions: Record<string, string> }>('/api/merchants/suggestions', { method: 'POST', body: { keys } }),

  /** Reads a PDF bank statement into transactions (SM-23). The PDF isn't kept. */
  readPdfStatement: (file: File) => {
    const body = new FormData()
    body.append('statement', file)
    return request<PdfStatement>('/api/statements/pdf', { method: 'POST', body })
  },

  /**
   * An irreversible fingerprint of a statement's sort code and account number
   * (SM-25), to match statements to accounts. The numbers go nowhere else.
   */
  statementFingerprint: (details: AccountDetails) =>
    request<{ fingerprint: string }>('/api/statements/fingerprint', { method: 'POST', body: details }),

  /** Adds these names to the shared catalogue, when the person leaves sharing on. */
  shareMerchants: (names: { key: string; name: string }[]) =>
    request<{ accepted: number; new: number }>('/api/merchants/shares', { method: 'POST', body: { names } }),

  /** Adds an import's monthly totals per shop to the anonymous statistics, when the person has opted in (SM-18). */
  shareSpending: (months: SpendingShare) =>
    request<{ added: number }>('/api/statistics/spending/shares', { method: 'POST', body: { months } }),

  /**
   * The latest month's anonymous averages, per shop and category (SM-19), and
   * whether this person shares — only they are shown how theirs compares.
   */
  spendingAverages: () =>
    request<{ month: string | null; shops: SpendFigure[]; categories: SpendFigure[]; sharing: boolean; minPeople: number }>(
      '/api/statistics/spending',
    ),

  /** Opting out: deletes everything this person has shared for spending statistics. */
  withdrawSpending: () => request<{ removed: number }>('/api/statistics/spending/shares', { method: 'DELETE' }),

  /** A merchant's category and whether it's sensitive; nothing is shared for one without a category. */
  adminSetMerchantProfile: (key: string, category: string | null, sensitive: boolean) =>
    request<{ key: string; category: string | null; sensitive: boolean }>('/api/admin/merchant-profiles', {
      method: 'PUT',
      body: { key, category, sensitive },
    }),

  /** One month's saved spending figures, the latest by default. */
  adminSpending: (month?: string) =>
    request<AdminSpending>(`/api/admin/spending${month ? `?${new URLSearchParams({ month })}` : ''}`),

  /** Signs in to the admin panel: an admin account's password and the code from its authenticator app. */
  adminLogin: (email: string, password: string, code: string) =>
    request<{ admin: AdminAccount }>('/api/admin/login', { method: 'POST', body: { email, password, code } }),

  /** Who is signed in to the panel; a 401 means nobody, or the session went quiet for too long. */
  adminMe: () => request<{ admin: AdminAccount }>('/api/admin/me'),

  adminLogout: () => request<void>('/api/admin/logout', { method: 'POST' }),

  adminMerchants: ({ filter, q, page }: { filter: AdminMerchantFilter; q: string; page: number }) =>
    request<{ items: AdminMerchant[]; total: number; threshold: number }>(
      `/api/admin/merchants?${new URLSearchParams({ filter, q, page: String(page) })}`,
    ),

  adminFlag: (id: string, flagged: boolean) =>
    request<{ id: string; flagged: boolean }>(`/api/admin/merchants/${encodeURIComponent(id)}/flag`, {
      method: 'POST',
      body: { flagged },
    }),

  nameSuggestions: () => request<{ names: NameSuggestion[] }>('/api/suggestions/names'),

  adminNames: ({ kind, q, page }: { kind: SuggestionKind | 'all'; q: string; page: number }) =>
    request<{ items: AdminNameSuggestion[]; total: number }>(
      `/api/admin/names?${new URLSearchParams({ kind: kind === 'all' ? '' : kind, q, page: String(page) })}`,
    ),

  adminAddName: (suggestion: NameSuggestion) =>
    request<AdminNameSuggestion>('/api/admin/names', { method: 'POST', body: suggestion }),

  adminUpdateName: (id: string, suggestion: NameSuggestion) =>
    request<AdminNameSuggestion>(`/api/admin/names/${encodeURIComponent(id)}`, { method: 'PUT', body: suggestion }),

  adminRemoveName: (id: string) => request<void>(`/api/admin/names/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Counts a name this person gave an item towards it becoming a suggestion, when they leave sharing on. */
  shareName: (suggestion: NameSuggestion) =>
    request<{ counted: boolean }>('/api/suggestions/names/shares', { method: 'POST', body: suggestion, keepalive: true }),

  adminLearnedNames: ({ filter, q, page }: { filter: AdminLearnedNameFilter; q: string; page: number }) =>
    request<{ items: AdminLearnedName[]; total: number; threshold: number }>(
      `/api/admin/learned-names?${new URLSearchParams({ filter, q, page: String(page) })}`,
    ),

  /** Makes a waiting name a suggestion, as written here. */
  adminApproveLearnedName: (id: string, suggestion: NameSuggestion) =>
    request<AdminNameSuggestion>(`/api/admin/learned-names/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: suggestion,
    }),

  adminRejectLearnedName: (id: string) =>
    request<void>(`/api/admin/learned-names/${encodeURIComponent(id)}/reject`, { method: 'POST' }),

  /** Deletes the account and everything in it, and ends the session. Needs the password again. */
  deleteAccount: (password: string) =>
    request<{ status: 'deleted' }>('/api/account', { method: 'DELETE', body: { password } }),
}
